/**
 * Finans işleminin panelden nasıl sonuçlandırılabileceği.
 *
 * Aynı soruyu iki ekran birden sorar — liste satırındaki hızlı karar düğmeleri
 * ve detay panelinin alt çubuğu — ve ikisi **aynı** yanıtı vermelidir: listede
 * "Onayla" gösterip panelde göstermemek (ya da tersi) yöneticiye yapılamayan
 * bir iş vaat eder, backend de isteği 409 ile geri çevirir. Kurallar bu yüzden
 * bileşenlerde değil burada durur.
 *
 * Karşılığı backend'dedir: services/admin_finance.py `APPROVAL_PROVIDERS`,
 * `CALLBACK_ONLY_PROVIDERS` ve `CALLBACK_ONLY_DEPOSIT_PROVIDERS`. Listeler
 * ayrışırsa panel bir şey söyler, uç başka şey yapar.
 */

// Sağlayıcıya iletilmeden önce yönetici onayı bekleyen çekimin durumu;
// backend'deki wizkasa.AWAITING_APPROVAL ile aynı kalmalıdır.
export const AWAITING_APPROVAL = 'onay_bekliyor';

// Sağlayıcının panelde görünen adı; backend'deki PROVIDER_LABEL sabitleriyle
// birebir aynıdır (teknik anahtar hiçbir yerde yeniden adlandırılmaz).
export const PROVIDER_LABELS = {
  cashmoney: 'CashMoney Pay',
  wizkasa: 'Wizkasa',
  hemensystem: 'Hemen Havale',
  hera: 'Hera',
  payurus: 'Payurus',
  ultrapay: 'UltraPay',
  linkhavale: 'Link Havale',
  vevopay: 'VEVO PAY',
  hizlicaparalar: 'HızlıcaParalar',
  hizlihavale: 'Hızlı Havale',
  zelvoq: 'Zelvoq',
  cryptobox: 'CryptoBox',
  paylux: 'Paylux',
  expay: 'ExPay',
  primevpay: 'PrimeVpay',
};

// Durum sorgulama ucu olan sağlayıcılar. Yol sağlayıcı başına ayrıdır ve
// backend'deki uç adlarıyla birebir aynı kalmalıdır
// (routers/<sağlayıcı>_router.py → `/admin/<sağlayıcı>/reconcile/{id}`).
// Hemen Havale ve HızlıcaParalar burada **yoktur**: ikisinin de durum
// sorgulama ucu yok, kaybolan bildirimde tek araç yöneticinin elle kararıdır.
export const RECONCILE_PATHS = {
  cashmoney: '/admin/cashmoney/reconcile',
  wizkasa: '/admin/wizkasa/reconcile',
  hera: '/admin/hera/reconcile',
  // Payurus'ta durum ucu vardır ama cevabının biçimi kılavuzda tanımlı
  // değildir: terminal bir sonuç okunamazsa kayda dokunulmaz ve yönetici
  // çekimi elle karara bağlayabilir.
  payurus: '/admin/payurus/reconcile',
  ultrapay: '/admin/ultrapay/reconcile',
  linkhavale: '/admin/linkhavale/reconcile',
  vevopay: '/admin/vevopay/reconcile',
  // Hızlı Havale'de sorgu bir yedek değil **asıl yoldur**: sağlayıcı
  // bildirimi imzalamaz, sonucu her zaman durum ucu belirler.
  hizlihavale: '/admin/hizlihavale/reconcile',
  // Zelvoq'un durum ucu (`POST /api/transaction/status`) hem yatırımı hem
  // çekimi belgelenmiş durumlarla döndürür ve sorgu **bizim referansımızla**
  // yapılır. Pencere 15 gündür; daha eskisi sorulamaz ve uç bunu sebebiyle
  // söyler.
  zelvoq: '/admin/zelvoq/reconcile',
  // CryptoBox'ta çekim durumu sorulup bildirimle aynı yoldan uygulanır;
  // yatırımda oturum ucu tutar vermez, yalnızca ödemesiz kapanışı söyler.
  cryptobox: '/admin/cryptobox/reconcile',
  // Paylux'ün `checkStatus` ucu hem yatırımı hem çekimi `statusLabel` ile
  // döndürür ve sorgu **bizim** işlem numaramızla yapılır. Bildirimin kendi
  // kimliği ve imzası olmadığı için sonucu zaten sık sık bu cevap belirler.
  paylux: '/admin/paylux/reconcile',
  // ExPay'in bildirimi imzasız ve durumsuzdur; sonucu her zaman
  // `/api/transaction/get` cevabı belirler ve sorgu **bizim** işlem
  // numaramızla yapılır.
  expay: '/admin/expay/reconcile',
  // PrimeVpay sonucu imzalı bildirimle gelir; sorgu yalnızca bildirimi
  // kaçan kayıt içindir ve **bizim** işlem numaramızla (`brand_tx_id`) yapılır.
  primevpay: '/admin/primevpay/reconcile',
};

// Yönü ne olursa olsun panelden karara bağlanamayan sağlayıcılar: sonucu
// callback belirler, elle karar aynı işlemi iki kez uygulardı.
const CALLBACK_ONLY_PROVIDERS = [
  'cashmoney', 'wizkasa', 'hera', 'ultrapay', 'linkhavale', 'vevopay', 'hizlihavale',
  'zelvoq', 'cryptobox', 'paylux', 'expay', 'primevpay',
];

// Yalnızca **yatırımı** callback ile kapanan sağlayıcılar. Çekim bunun
// dışındadır: tutar talep anında bakiyeden düşer ve kaybolan bir bildirimde
// parayı çözecek tek araç yöneticinin elle kararıdır.
const CALLBACK_ONLY_DEPOSIT_PROVIDERS = [
  'hemensystem', 'payurus', 'hizlicaparalar',
];

export const providerLabel = (provider) => (
  PROVIDER_LABELS[provider] || 'Sağlayıcı');

/**
 * İşlemin karar/mutabakat yetenekleri.
 *
 * `canResolve`   → panelden onaylanıp reddedilebilir mi,
 * `canReconcile` → sağlayıcının durum ucundan sorgulanabilir mi,
 * `locallyClosed`→ kaydı sağlayıcı değil **biz** kapattık (geri alınabilir).
 */
export const financeDecision = (item) => {
  const provider = String(item?.provider || '') || 'manual';
  const type = item?.type;
  const financeRow = ['deposit', 'withdraw'].includes(type);
  const pending = item?.status === 'beklemede' && financeRow;
  const providerManaged = Boolean(item?.provider) && provider !== 'manual';
  // Sağlayıcıya iletilmemiş çekim yönetici kararını bekler: onaylanınca talep
  // sağlayıcıya açılır, reddedilince tutar oyuncuya iade edilir.
  const awaitingApproval = pending
    && type === 'withdraw'
    && item?.provider_status === AWAITING_APPROVAL;
  const callbackOnly = CALLBACK_ONLY_PROVIDERS.includes(provider)
    || (type === 'deposit' && CALLBACK_ONLY_DEPOSIT_PROVIDERS.includes(provider));
  // İletilmiş kayıtta sonucu callback belirler; elle karar verilirse backend
  // 409 döner, o yüzden karar düğmeleri yerine mutabakat gelir.
  const canResolve = pending && (!callbackOnly || awaitingApproval);
  // Sağlayıcının durum ucu olmadığı için elle karara kalan çekim: düğmeler
  // görünür ama gerekçesi yöneticiye yazılmalıdır.
  const manuallyResolvable = canResolve
    && providerManaged
    && !awaitingApproval
    && type === 'withdraw'
    && CALLBACK_ONLY_DEPOSIT_PROVIDERS.includes(provider);
  // **Bizim** kapattığımız yatırım gerçek bir terminal karar değildir: hem
  // ödeme süresi hem de "sağlayıcıda kayıt yok" cevabı bizim varsayımımızdır.
  // Son dakikada ödeyip bildirimi geç gelen oyuncunun kaydı bu yüzden yine
  // sorgulanabilir. Ayrımı backend bildirir (`locally_closed`, bkz.
  // wizkasa.is_locally_closed_deposit); alan yoksa eski davranış korunur.
  const locallyClosed = item?.locally_closed === undefined
    ? (type === 'deposit'
      && item?.status === 'reddedildi'
      && item?.provider_status === 'expired')
    : Boolean(item?.locally_closed);
  const reconcilePath = RECONCILE_PATHS[provider] || '';
  const canReconcile = (pending || locallyClosed)
    && !awaitingApproval
    && Boolean(reconcilePath);

  return {
    provider,
    providerLabel: providerLabel(provider),
    pending,
    providerManaged,
    awaitingApproval,
    manuallyResolvable,
    canResolve,
    canReconcile,
    reconcilePath,
    locallyClosed,
    locallyExpired: locallyClosed && item?.provider_status === 'expired',
  };
};

export default financeDecision;
